import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '../../../lib/api-fetcher';
import type { OperationalTask, FailedGlPosting, TaskStatus, TaskPriority, OperationalTaskType } from '../types';

export function useOperationsInbox(filters?: { status?: TaskStatus }) {
  return useQuery({
    queryKey: ['operations', 'inbox', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      const qs = params.toString();
      return fetcher.get<{ data: OperationalTask[]; meta: { total: number; page: number; limit: number } }>(
        `/staff/v1/operations/inbox${qs ? `?${qs}` : ''}`,
      );
    },
  });
}

export function useOperationsTasks(filters?: {
  queueName?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  taskType?: OperationalTaskType;
}) {
  return useQuery({
    queryKey: ['operations', 'tasks', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.queueName) params.append('queueName', filters.queueName);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.priority) params.append('priority', filters.priority);
      if (filters?.taskType) params.append('taskType', filters.taskType);
      const qs = params.toString();
      return fetcher.get<{ data: OperationalTask[]; meta: { total: number; page: number; limit: number } }>(
        `/staff/v1/operations/tasks${qs ? `?${qs}` : ''}`,
      );
    },
  });
}

export function useResolveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolutionNotes }: { id: string; resolutionNotes: string }) =>
      fetcher.post(`/staff/v1/operations/tasks/${id}/resolve`, { resolutionNotes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

export function useEscalateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      fetcher.post(`/staff/v1/operations/tasks/${id}/escalate`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

export function useFailedGlPostings(filters?: { status?: 'UNRESOLVED' | 'RESOLVED' }) {
  return useQuery({
    queryKey: ['operations', 'failed-gl', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      const qs = params.toString();
      return fetcher.get<{ data: FailedGlPosting[]; meta: { total: number; page: number; limit: number } }>(
        `/staff/v1/operations/failed-gl-postings${qs ? `?${qs}` : ''}`,
      );
    },
  });
}

export function useResolveFailedGlPosting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolutionNotes }: { id: string; resolutionNotes: string }) =>
      fetcher.post(`/staff/v1/operations/failed-gl-postings/${id}/resolve`, { resolutionNotes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operations', 'failed-gl'] });
    },
  });
}
